import type { ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'
import get from 'lodash-es/get'
import { type JSX, useEffect, useEffectEvent, useState } from 'react'

import { useFormikChildListenerContext } from '../../forms/context/formikChildListenerContext'
import { useFormikContext } from '../../hooks/useFormikContext'
import { Icon } from '../../structure/icon/Icon'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import { useIsDisabledInput } from '../formikUtils/disabledInputContext'

export const withFormikInputDiffing = <P extends { name: ValidatedKey }>(
  WrappedComponent: (props: P) => JSX.Element | null,
) => {
  function FormikInputDiffingWrapper(props: P & JSX.IntrinsicAttributes) {
    const [field] = useField({ name: props.name })
    const { initialValues, status } = useFormikContext()
    const isDisabledInput = useIsDisabledInput()
    const isDiffing = status.shouldShowChangesInForm
    const existingValue = useExistingValuesContext()?.getExistingValue?.(
      props.name,
    )

    const originalValue = get(initialValues, props.name as string)

    const [shouldHideChange, setShouldHideChange] = useState<boolean>(false)

    const handleShouldSetChanges = useEffectEvent(() => {
      if (isDiffing && field.value !== originalValue) {
        setShouldHideChange(false)
      } else {
        setShouldHideChange(true)
      }
    })

    useEffect(() => {
      if (isDiffing) {
        handleShouldSetChanges()
      } else {
        setShouldHideChange(false)
      }
    }, [isDiffing])

    const { onFormikChildChange } = useFormikChildListenerContext()

    useEffect(() => {
      onFormikChildChange({ name: props.name, action: 'add' })

      return () => {
        onFormikChildChange({ name: props.name, action: 'remove' })
      }
    }, [onFormikChildChange, props.name])

    // Set by FormikFieldTable for unchanged cells of changed rows, which must stay visible in show-changes mode
    if (isDisabledInput) {
      return <WrappedComponent {...props} disabled />
    }

    return isDiffing ? (
      <>
        {shouldHideChange ? null : (
          <div className="flex gap-2">
            <WrappedComponent
              {...props}
              value={originalValue ?? existingValue}
              defaultValue={undefined}
              existingValueConfig={{
                existingValue: undefined,
              }}
              hasRightColumnAlways={false}
              validationText={undefined}
              name={`$diff.${props.name}`}
              disabled
            />
            <Icon className="flex self-center" name="ArrowRight" />
            <WrappedComponent
              {...props}
              value={field.value}
              defaultValue={undefined}
            />
          </div>
        )}
      </>
    ) : (
      <WrappedComponent {...props} />
    )
  }

  return FormikInputDiffingWrapper
}
